/**
 * Downscale + recompress an image on the client before it's uploaded, so we
 * never store 5–20MB phone originals — the "full-res" we keep is already a
 * sensible size. Runs in the browser (web + public fill hosts); best-effort,
 * falling back to the original file on any failure or when it wouldn't help.
 *
 * The uploaded image is the source the media-worker later resizes further for
 * previews / PDFs, and the only thing "download original" returns — so this cap
 * is the effective max quality kept.
 */

// Cap the longest edge; re-encode to JPEG at this quality.
const MAX_UPLOAD_DIMENSION = 2048;
const UPLOAD_JPEG_QUALITY = 0.82;
// A small JPEG under this is already fine — don't bother re-encoding it.
const SKIP_IF_JPEG_UNDER_BYTES = 1_500_000;

export async function downscaleImageForUpload(file: File): Promise<File> {
  const type = (file.type || "").toLowerCase();
  if (!type.startsWith("image/")) return file;
  // Vector / animated formats: rasterising would lose the vector or flatten
  // the animation — leave them untouched.
  if (type === "image/svg+xml" || type === "image/gif") return file;
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") {
    return file;
  }

  try {
    // `from-image` applies EXIF orientation so the stored image is upright.
    // (Cast: the value is valid at runtime but missing from older DOM lib types.)
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    } as any);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    const scale = Math.min(1, MAX_UPLOAD_DIMENSION / longest);

    // Already small AND already a compact JPEG → keep as-is.
    if (scale >= 1 && type === "image/jpeg" && file.size < SKIP_IF_JPEG_UNDER_BYTES) {
      bitmap.close?.();
      return file;
    }

    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", UPLOAD_JPEG_QUALITY),
    );
    // No blob, or re-encoding didn't shrink it → upload the original.
    if (!blob || blob.size >= file.size) return file;

    const base = file.name.replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${base}.jpg`, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    // HEIC that the browser can't decode, an oversized canvas, etc. — upload
    // the original; the server thumbnailer still handles it.
    return file;
  }
}
